'use client'

import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Loader2,
  Check,
  X,
  DollarSign,
  RefreshCw,
  Plus,
  CheckCircle,
  Eye,
  EyeOff,
} from 'lucide-react'

/** Convert microUSD to display dollars (e.g. 80000 → "0.08") */
function microToDisplay(micro: number): string {
  return (micro / 1_000_000).toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
}

/** Convert display dollars to microUSD (e.g. "0.08" → 80000) */
function displayToMicro(display: string): number {
  const val = parseFloat(display)
  if (isNaN(val) || val < 0) return 0
  return Math.round(val * 1_000_000)
}

interface Webhook {
  id: string
  name: string
  url: string
  isDisabled: boolean
}

export interface ElevenLabsAccountData {
  id?: string
  name: string
  api_key_masked?: string | null
  max_concurrency: number
  max_agents: number
  cost_per_minute_microusd: number
  webhook_id: string | null
  webhook_name: string | null
  has_webhook_secret: boolean
  is_default: boolean
}

interface ElevenLabsAccountDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  account?: ElevenLabsAccountData | null
  onSaved: () => void
}

export function ElevenLabsAccountDialog({
  open,
  onOpenChange,
  account,
  onSaved,
}: ElevenLabsAccountDialogProps) {
  const { fetchWithAuth } = useAuth()
  const isEdit = !!account?.id
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [name, setName] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [costPerMinute, setCostPerMinute] = useState('0.08')
  const [maxConcurrency, setMaxConcurrency] = useState(10)
  const [maxAgents, setMaxAgents] = useState(25)
  const [isDefault, setIsDefault] = useState(false)

  // Webhook state
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [isLoadingWebhooks, setIsLoadingWebhooks] = useState(false)
  const [selectedWebhookId, setSelectedWebhookId] = useState('')
  const [selectedWebhookName, setSelectedWebhookName] = useState('')
  const [newWebhookName, setNewWebhookName] = useState('')
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false)
  const [showCreateWebhook, setShowCreateWebhook] = useState(false)
  const [webhookSecret, setWebhookSecret] = useState('')
  const [showWebhookSecret, setShowWebhookSecret] = useState(false)
  const [hasWebhookSecret, setHasWebhookSecret] = useState(false)

  // Reset form when dialog opens (useEffect catches programmatic open changes)
  useEffect(() => {
    if (!open) return
    if (account) {
      setName(account.name)
      setApiKey('')
      setCostPerMinute(microToDisplay(account.cost_per_minute_microusd))
      setMaxConcurrency(account.max_concurrency)
      setMaxAgents(account.max_agents)
      setIsDefault(account.is_default)
      setSelectedWebhookId(account.webhook_id || '')
      setSelectedWebhookName(account.webhook_name || '')
      setHasWebhookSecret(account.has_webhook_secret)
    } else {
      setName('')
      setApiKey('')
      setCostPerMinute('0.08')
      setMaxConcurrency(10)
      setMaxAgents(25)
      setIsDefault(false)
      setSelectedWebhookId('')
      setSelectedWebhookName('')
      setHasWebhookSecret(false)
    }
    setShowApiKey(false)
    setWebhookSecret('')
    setShowWebhookSecret(false)
    setWebhooks([])
    setNewWebhookName('')
    setShowCreateWebhook(false)
    setError(null)
  }, [open, account])

  const accountId = account?.id

  // The endpoint the selected webhook posts to: prefer the loaded webhook's
  // real URL, else fall back to our canonical receiver path.
  const webhookEndpoint =
    webhooks.find((w) => w.id === selectedWebhookId)?.url ??
    (typeof window !== 'undefined'
      ? `${window.location.origin}/api/webhooks/elevenlabs`
      : '/api/webhooks/elevenlabs')

  const loadWebhooks = async () => {
    if (!accountId) return
    setIsLoadingWebhooks(true)
    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/webhooks`,
      )
      if (res.ok) {
        const data = await res.json()
        const loaded: Webhook[] = data.webhooks || []
        setWebhooks(loaded)

        // Auto-fill webhook name if webhook is already selected but name is missing
        if (selectedWebhookId && !selectedWebhookName) {
          const match = loaded.find((w) => w.id === selectedWebhookId)
          if (match?.name) {
            setSelectedWebhookName(match.name)
            // Persist name to DB
            fetchWithAuth(
              `/api/admin/billing/elevenlabs/accounts/${accountId}/webhooks`,
              {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ webhook_id: selectedWebhookId, webhook_name: match.name }),
              },
            ).catch(() => {/* non-critical */})
          }
        }
      } else {
        const data = await res.json()
        setError(data.error || 'Failed to load webhooks')
      }
    } catch {
      setError('Failed to load webhooks')
    } finally {
      setIsLoadingWebhooks(false)
    }
  }

  const handleCreateWebhook = async () => {
    if (!accountId || !newWebhookName.trim()) return
    setIsCreatingWebhook(true)
    try {
      const webhookUrl = `${window.location.origin}/api/webhooks/elevenlabs`
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/webhooks`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: newWebhookName, url: webhookUrl }),
        },
      )
      if (res.ok) {
        const data = await res.json()
        setSelectedWebhookId(data.webhookId)
        setSelectedWebhookName(newWebhookName.trim())
        setHasWebhookSecret(data.hasSecret)
        setNewWebhookName('')
        setShowCreateWebhook(false)
        loadWebhooks()
      } else {
        const data = await res.json()
        setError(data.error || 'Failed to create webhook')
      }
    } catch {
      setError('Failed to create webhook')
    } finally {
      setIsCreatingWebhook(false)
    }
  }

  const handleSelectWebhook = async (webhookId: string) => {
    if (!accountId) return
    const webhookName = webhooks.find((w) => w.id === webhookId)?.name || ''
    setSelectedWebhookId(webhookId)
    setSelectedWebhookName(webhookName)
    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/webhooks`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ webhook_id: webhookId, webhook_name: webhookName }),
        },
      )
      if (!res.ok) {
        const data = await res.json()
        setError(data.error || 'Failed to select webhook')
      }
    } catch {
      setError('Failed to select webhook')
    }
  }

  const handleSaveWebhookSecret = async () => {
    if (!accountId || !webhookSecret.trim() || !selectedWebhookId) return
    setSaving(true)
    try {
      const res = await fetchWithAuth(
        `/api/admin/billing/elevenlabs/accounts/${accountId}/webhooks`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            webhook_id: selectedWebhookId,
            webhook_secret: webhookSecret.trim(),
          }),
        },
      )
      if (res.ok) {
        setHasWebhookSecret(true)
        setWebhookSecret('')
      } else {
        const data = await res.json()
        setError(data.error || 'Failed to save webhook secret')
      }
    } catch {
      setError('Failed to save webhook secret')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required')
      return
    }
    if (!isEdit && !apiKey.trim()) {
      setError('API key is required')
      return
    }
    const costMicro = displayToMicro(costPerMinute)
    if (costMicro <= 0) {
      setError('Cost per minute must be greater than 0')
      return
    }

    setSaving(true)
    setError(null)
    try {
      if (isEdit) {
        // PATCH existing account
        const patchBody: Record<string, unknown> = {
          name: name.trim(),
          max_concurrency: maxConcurrency,
          max_agents: maxAgents,
          cost_per_minute_microusd: costMicro,
          is_default: isDefault,
        }
        if (apiKey.trim()) {
          patchBody.api_key = apiKey.trim()
        }

        const res = await fetchWithAuth(
          `/api/admin/billing/elevenlabs/accounts/${accountId}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(patchBody),
          },
        )
        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.error || 'Failed to update account')
        }
      } else {
        // POST new account
        const res = await fetchWithAuth(
          '/api/admin/billing/elevenlabs/accounts',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: name.trim(),
              api_key: apiKey.trim(),
              max_concurrency: maxConcurrency,
              max_agents: maxAgents,
              cost_per_minute_microusd: costMicro,
              is_default: isDefault,
            }),
          },
        )
        if (!res.ok) {
          const data = await res.json()
          throw new Error(data.error || 'Failed to add account')
        }
      }

      onOpenChange(false)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit ElevenLabs Account' : 'Add ElevenLabs Account'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Editing ${account?.name}`
              : 'Add a new ElevenLabs account to the voice provider pool'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="grid gap-4 py-2">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="el_name">Account Name</Label>
            <Input
              id="el_name"
              placeholder="elevenlabs-prod-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="el_api_key">
              {isEdit ? 'Update API Key' : 'API Key'}
            </Label>
            <div className="relative">
              <Input
                id="el_api_key"
                type={showApiKey ? 'text' : 'password'}
                placeholder={
                  isEdit
                    ? 'Enter new API key to update...'
                    : 'sk-...'
                }
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="pr-10"
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {isEdit && account?.api_key_masked && (
              <p className="text-xs text-muted-foreground">
                <span>Current:</span> {account.api_key_masked}
              </p>
            )}
          </div>

          {/* Cost per minute */}
          <div className="space-y-2">
            <Label htmlFor="el_cost">Cost per Minute (USD)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="el_cost"
                type="text"
                inputMode="decimal"
                placeholder="0.08"
                className="pl-9"
                value={costPerMinute}
                onChange={(e) => setCostPerMinute(e.target.value)}
                autoComplete="off"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              ElevenLabs account tariff rate
            </p>
          </div>

          {/* Concurrency / Agents */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="el_concurrency">Max Concurrency</Label>
              <Input
                id="el_concurrency"
                type="number"
                value={maxConcurrency}
                onChange={(e) => setMaxConcurrency(Number(e.target.value))}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="el_agents">Max Agents</Label>
              <Input
                id="el_agents"
                type="number"
                value={maxAgents}
                onChange={(e) => setMaxAgents(Number(e.target.value))}
                autoComplete="off"
              />
            </div>
          </div>

          {/* Default toggle */}
          <div className="flex items-center space-x-2">
            <Switch
              id="el_default"
              checked={isDefault}
              onCheckedChange={setIsDefault}
            />
            <Label htmlFor="el_default">Set as default for new tenants</Label>
          </div>

          {/* Webhook Configuration — only for edit mode (account must exist first) */}
          {isEdit && (
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-0.5">
                  <h4 className="text-sm font-semibold">Post-Call Webhook</h4>
                  <p className="text-xs text-muted-foreground">
                    Webhooks are created in ElevenLabs. Load the account&apos;s list
                    and select the one that reports calls back to us.
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={loadWebhooks}
                  disabled={isLoadingWebhooks}
                >
                  {isLoadingWebhooks ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Load from ElevenLabs
                    </>
                  )}
                </Button>
              </div>

              {/* Current webhook status */}
              {selectedWebhookId && (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
                    <span className="text-green-700 whitespace-nowrap">
                      Webhook configured:
                      {selectedWebhookName && (
                        <span className="font-medium ml-1">{selectedWebhookName}</span>
                      )}
                    </span>
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground break-all">
                    <span>Endpoint:</span>{' '}
                    <code className="bg-muted px-1 rounded">{webhookEndpoint}</code>
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground break-all">
                    <span>ID:</span> <code className="bg-muted px-1 rounded">{selectedWebhookId}</code>
                  </div>
                </div>
              )}

              {/* Webhook selector */}
              {webhooks.length > 0 && (
                <div className="space-y-2">
                  <Label>Select Webhook</Label>
                  <Select
                    value={selectedWebhookId}
                    onValueChange={handleSelectWebhook}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a webhook..." />
                    </SelectTrigger>
                    <SelectContent>
                      {webhooks.map((wh) => (
                        <SelectItem key={wh.id} value={wh.id}>
                          <span>{wh.name}</span>
                          {wh.isDisabled && (
                            <span className="text-xs text-yellow-600 ml-1">
                              (disabled)
                            </span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Create new webhook — secondary path, collapsed by default */}
              {!showCreateWebhook ? (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-muted-foreground"
                  onClick={() => setShowCreateWebhook(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  <span>Don&apos;t have one? Create a webhook here instead</span>
                </Button>
              ) : (
                <div className="space-y-2 rounded-md bg-muted/40 p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs">New webhook name</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-xs text-muted-foreground"
                      onClick={() => {
                        setShowCreateWebhook(false)
                        setNewWebhookName('')
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="e.g. prod-post-call"
                      value={newWebhookName}
                      onChange={(e) => setNewWebhookName(e.target.value)}
                      autoComplete="off"
                    />
                    <Button
                      onClick={handleCreateWebhook}
                      disabled={isCreatingWebhook || !newWebhookName.trim()}
                      variant="outline"
                      size="sm"
                    >
                      {isCreatingWebhook ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <Plus className="h-4 w-4 mr-1" />
                          Create
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    <span>Creates a webhook in ElevenLabs pointing to:</span>{' '}
                    <code className="bg-muted px-1 rounded">
                      {typeof window !== 'undefined'
                        ? window.location.origin
                        : ''}
                      <span>/api/webhooks/elevenlabs</span>
                    </code>
                  </p>
                </div>
              )}

              {/* HMAC Secret */}
              {selectedWebhookId && (
                <div className="space-y-2">
                  <Label htmlFor="el_hmac">
                    {hasWebhookSecret
                      ? 'Update HMAC Secret'
                      : 'HMAC Secret (for signature verification)'}
                  </Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Input
                        id="el_hmac"
                        type={showWebhookSecret ? 'text' : 'password'}
                        placeholder={
                          hasWebhookSecret
                            ? 'Enter new secret to update...'
                            : 'Paste secret from ElevenLabs...'
                        }
                        value={webhookSecret}
                        onChange={(e) => setWebhookSecret(e.target.value)}
                        className="pr-10"
                        autoComplete="new-password"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-0 top-0 h-full px-3"
                        onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                      >
                        {showWebhookSecret ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                    <Button
                      onClick={handleSaveWebhookSecret}
                      disabled={saving || !webhookSecret.trim()}
                      variant="outline"
                      size="sm"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {hasWebhookSecret && (
                    <p className="text-xs text-green-600">
                      <CheckCircle className="h-3 w-3 inline mr-1" />
                      <span>HMAC secret configured — webhook signatures will be
                      verified</span>
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="mr-2 h-4 w-4" />
            <span>Cancel</span>
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            <span>{isEdit ? 'Save Changes' : 'Add Account'}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
